export const normalizeReraFromRaw = (sections = []) => {
  const normalizeKey = (str = "") =>
    str
      .replace(/\u00A0/g, " ")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const findSection = (type, keyword) =>
    sections.find(
      (s) =>
        s.type === type && normalizeKey(s.title).includes(normalizeKey(keyword))
    );

  /* ---------------- CASE DETAIL ---------------- */

  const caseDetailSection = findSection("key-value", "case detail");
  const rawCaseDetail = caseDetailSection?.data || {};
  const caseDetail = normalizeDataObject(rawCaseDetail);

  const caseNumber = caseDetail.caseno || rawCaseDetail["Case No"] || "N/A";

  const filingNo = caseDetail.filingno || "";

  const caseFiledDate =
    caseDetail.dateoffiling || caseDetail.registrationdate || "";

  /* ---------------- CASE YEAR ---------------- */

  let caseYear = "N/A";

  // 1️⃣ Try extracting from caseNumber (Appeal - 49/2024)
  if (caseNumber && /\d{4}/.test(caseNumber)) {
    const match = caseNumber.match(/(19|20)\d{2}/);
    if (match) caseYear = match[0];
  }

  // 2️⃣ Fallback: extract from filing date (21/06/2023)
  if (caseYear === "N/A" && caseFiledDate) {
    const match = caseFiledDate.match(/(19|20)\d{2}/);
    if (match) caseYear = match[0];
  }

  const status = (caseDetail.casestatus || "pending").toLowerCase();

  /* ---------------- PARTY DETAILS ---------------- */

  let petitionerName = "N/A";
  let respondentName = "N/A";
  const allPetitioners = [];
  const allRespondents = [];

  /* ---------------- LEGAL REPRESENTATIVES (ADVOCATES) ---------------- */

  let petitionerAdvocate = "";
  let respondentAdvocate = "";
  const allPetitionerAdvocates = [];
  const allRespondentAdvocates = [];

  sections
    .filter((s) => s.type === "split-table")
    .forEach((section) => {
      const isLegalRepSection = normalizeKey(section.title).includes("legalrepresentative");
      const isPartySection = normalizeKey(section.title).includes("partydetail");

      section.data.forEach((table) => {
        // Check headers to determine what type of data this table contains
        const headerKeys = (table.headers || []).map(h => normalizeKey(h));

        table.rows.forEach((row) => {
          const normalizedRow = normalizeDataObject(row);

          // Party Details - Petitioner/Appellant names
          if (normalizedRow.applicantappellantsname) {
            if (petitionerName === "N/A") {
              petitionerName = normalizedRow.applicantappellantsname;
            } else {
              allPetitioners.push({ name: normalizedRow.applicantappellantsname });
            }
          }

          // Party Details - Respondent names
          if (normalizedRow.respodentname) {
            if (respondentName === "N/A") {
              respondentName = normalizedRow.respodentname;
            } else {
              allRespondents.push({ name: normalizedRow.respodentname });
            }
          }

          // Legal Representative - Petitioner/Appellant advocates
          if (normalizedRow.applicantappellantslegalrepresentativename) {
            const advocateName = normalizedRow.applicantappellantslegalrepresentativename;
            if (!petitionerAdvocate) {
              petitionerAdvocate = advocateName;
            } else {
              allPetitionerAdvocates.push({ name: advocateName });
            }
          }

          // Legal Representative - Respondent advocates
          if (normalizedRow.respodentlegalrepresentativename) {
            const advocateName = normalizedRow.respodentlegalrepresentativename;
            if (!respondentAdvocate) {
              respondentAdvocate = advocateName;
            } else {
              allRespondentAdvocates.push({ name: advocateName });
            }
          }
        });
      });
    });

  /* ---------------- HEARING DETAILS ---------------- */

  const nextHearingSection = findSection("key-value", "next hearing");
  const nextHearing =
    normalizeDataObject(nextHearingSection?.data || {}).hearingdate || "";

  const lastHearingSection = findSection("key-value", "last hearing");
  const previousHearing =
    normalizeDataObject(lastHearingSection?.data || {}).hearingdate || "";

  /* ---------------- CASE HISTORY ---------------- */

  const historySection = findSection("grid", "case history");
  const historyRows = historySection?.rows || [];

  const caseHistory = historyRows.map((row) => {
    const r = normalizeDataObject(row);
    return {
      hearingDate: r.hearingdate || "N/A",
      businessOnDate: r.courtno || "N/A",
      judge: "N/A",
      purpose: r.purpose || "N/A",
      causeListType: "N/A",
    };
  });

  return {
    caseNumber,
    filingNo,
    caseYear,
    caseFiledDate,
    petitionerName,
    respondentName,
    allPetitioners,
    allRespondents,
    petitionerAdvocate,
    respondentAdvocate,
    allPetitionerAdvocates,
    allRespondentAdvocates,
    nextHearingDate: nextHearing,
    previousHearingDate: previousHearing,
    caseHistory,
    status,
    caseType: "RERA",
    court: "RERA",
    courtName: "RERA",
  };
};

// UP RERA (up-rera.in / Regulatory Authority) — Complaint Status detail.
// The backend returns a single rich `detail` record (not the appellate
// `sections` array), so we map it to the same common case shape the
// CaseDetails page consumes. UP-RERA-specific extras are carried separately in
// payload.upRera (see UpReraComplaintDetail export).
export const normalizeUpReraComplaint = (detail = {}) => {
  const c = detail.complainant || {};
  const complaintNo = detail.complaint_number || "";

  // Year: prefer the complaint number, fall back to the registration date.
  let caseYear = "N/A";
  const yearMatch =
    (complaintNo.match(/(19|20)\d{2}/) ||
      String(detail.registration_date || "").match(/(19|20)\d{2}/) ||
      [])[0];
  if (yearMatch) caseYear = yearMatch;

  // Next hearing date is embedded in hearing_details, e.g.
  // "( Next Hearing Date - 14-07-2026 ) Hearing Officer - Bench 2 Lucknow ..."
  let nextHearingDate = "";
  const nh = String(detail.hearing_details || "").match(
    /Next\s*Hearing\s*Date\s*[-:]?\s*([0-9]{1,2}[-/][0-9]{1,2}[-/][0-9]{4})/i
  );
  if (nh) nextHearingDate = nh[1];

  const status = String(detail.status || "pending")
    .toLowerCase()
    .includes("dispos")
    ? "disposed"
    : "pending";

  const petitionerName = c.name || "N/A";
  // The respondent in a complaint is the promoter/builder being complained
  // against. We only reliably have "Promoter" (complaint_against) here.
  const respondentName = detail.complaint_against || "N/A";

  return {
    caseNumber: complaintNo,
    caseNo: complaintNo,
    filingNo: complaintNo,
    filingNumber: complaintNo,
    caseYear,
    caseFiledDate: detail.registration_date || "",
    petitionerName,
    respondentName,
    allPetitioners: [{ name: petitionerName }],
    allRespondents: [{ name: respondentName }],
    petitionerAdvocate: "",
    respondentAdvocate: "",
    allPetitionerAdvocates: [],
    allRespondentAdvocates: [],
    nextHearingDate,
    previousHearingDate: "",
    caseHistory: [],
    status,
    caseType: "RERA",
    court: "RERA",
    courtName: "RERA - UTTAR PRADESH",
  };
};

export const normalizeHaryanaFromRaw = (data = {}) => {
  const sections = data.sections || {};
  const complaintNo = data.complaintNo || "";

  // Find the complaint detail section (key like "complaint_detail_rera_grg_3141_2024")
  const detailKey = Object.keys(sections).find((k) =>
    k.startsWith("complaint_detail")
  );
  const detailRows = sections[detailKey] || [];
  const detail = detailRows[0] || {};

  const listings = sections.complaint_listing_details || [];
  const documents = sections.documents_submitted || [];

  // Extract party names from "Party Dtls" (e.g., "KASVI JALOTA V/S MILLENNIUM DIPLOMATS PVT.LTD.")
  let petitionerName = "N/A";
  let respondentName = "N/A";
  if (detail["Party Dtls"]) {
    const parts = detail["Party Dtls"].split(/\s+V\/S\s+/i);
    petitionerName = parts[0]?.trim() || "N/A";
    respondentName = parts[1]?.trim() || "N/A";
  }

  // Extract year from complaintNo (e.g., "RERA-GRG-3141-2024")
  let caseYear = "N/A";
  const yearMatch = complaintNo.match(/(19|20)\d{2}/);
  if (yearMatch) caseYear = yearMatch[0];

  // Next hearing date
  const nextHearingDate = detail["Next Date of Hearing"] || "";

  // Previous hearing date (most recent listing that is not the next hearing)
  const previousHearingDate = listings[0]?.["Date of Hearing"] || "";

  // Case history from listings
  const caseHistory = listings.map((l) => ({
    hearingDate: l["Date of Hearing"] || "N/A",
    businessOnDate: l.Bench || "N/A",
    judge: l.Bench || "N/A",
    purpose: l["Proceedings of the day"] || "N/A",
    causeListType: l.Status || "N/A",
  }));

  const status = (detail["Current Status"] || "pending").toLowerCase();

  return {
    caseNumber: complaintNo,
    filingNo: complaintNo,
    caseYear,
    caseFiledDate: "",
    petitionerName,
    respondentName,
    allPetitioners: [{ name: petitionerName }],
    allRespondents: [{ name: respondentName }],
    petitionerAdvocate: detail["Self / Adv Name"] || "",
    respondentAdvocate: "",
    allPetitionerAdvocates: detail["Self / Adv Name"]
      ? [{ name: detail["Self / Adv Name"] }]
      : [],
    allRespondentAdvocates: [],
    nextHearingDate,
    previousHearingDate,
    caseHistory,
    status,
    caseType: "RERA",
    court: "RERA",
    courtName: "RERA - HARYANA",
  };
};

function normalizeDataObject(obj = {}) {
  const normalizeKey = (str = "") =>
    str
      .replace(/\u00A0/g, " ")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    out[normalizeKey(k)] = v;
  });
  return out;
}
